import type { CSSProperties } from 'react';

// Images live in two places: `src/images` for the ones components import, `content` for the ones
// articles name in their front matter. Each tree is globbed twice, once for the file Vite emits and
// once for the `placeholder` preset in `vite.config.ts`, so a placeholder can be found from the URL
// rather than threaded alongside it. Vite reads the patterns statically, so both calls spell them
// out rather than share a constant.

/** Every image, keyed by its path here. `no-inline` keeps small ones out of data URIs, which the
 * feed and `og:image` cannot use. */
export const imageUrls = import.meta.glob<string>(
  ['../images/**/*.{jpg,jpeg,png,webp,avif,gif}', '../../content/**/*.{jpg,jpeg,png,webp,avif,gif}'],
  { eager: true, query: '?url&no-inline', import: 'default' },
);

const placeholders = import.meta.glob<string>(
  ['../images/**/*.{jpg,jpeg,png,webp,avif,gif}', '../../content/**/*.{jpg,jpeg,png,webp,avif,gif}'],
  { eager: true, query: '?placeholder', import: 'default' },
);

const byUrl = new Map(Object.entries(imageUrls).map(([path, url]) => [url, placeholders[path]]));

// The marketing photos at several widths, so a phone does not download a desktop-sized image.
const avifSources = import.meta.glob<string>(['../images/marketing/*.jpg', '!../images/marketing/iso-*'], {
  eager: true,
  query: '?photo&format=avif',
  import: 'default',
});

const jpegSources = import.meta.glob<string>(['../images/marketing/*.jpg', '!../images/marketing/iso-*'], {
  eager: true,
  query: '?photo&format=jpg',
  import: 'default',
});

const sourcesByUrl = new Map(
  Object.entries(avifSources).map(([path, avif]) => [imageUrls[path], { avif, jpeg: jpegSources[path]! }]),
);

/** The AVIF and JPEG `srcset` values for a marketing photo, or undefined for any other image. */
export function photoSources(src: string) {
  return sourcesByUrl.get(src);
}

/**
 * Paints a blurred stand-in behind an image while it loads. Goes on the element wrapping the
 * `<img>`, which already sets the aspect ratio, so the stand-in is framed the same as the photo.
 * Undefined for a URL not built here, so a caller can pass one through without checking.
 */
export function blurBackground(src: string | null | undefined): CSSProperties | undefined {
  const placeholder = src ? byUrl.get(src) : undefined;
  if (!placeholder) return undefined;

  return { backgroundImage: `url(${placeholder})`, backgroundSize: 'cover', backgroundPosition: 'center' };
}
